import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import './StreaksChallenges.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function StreaksChallenges({ factFictionMode, navigateTo }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [streak, setStreak] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [recording, setRecording] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [streakData, challengeData] = await Promise.all([
        apiFetchJson(instance, '/api/gamification/streak'),
        apiFetchJson(instance, '/api/gamification/challenges'),
      ]);
      setStreak(streakData);
      setChallenges(unwrap(challengeData));
    } catch (err) {
      setError(err.message || 'Failed to load streaks and challenges');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRecordStreak = async () => {
    setRecording(true);
    try {
      const data = await apiFetchJson(instance, '/api/gamification/streak/record', { method: 'POST' });
      setStreak(data);
    } catch (err) {
      setError(err.message || 'Failed to record streak');
    } finally {
      setRecording(false);
    }
  };

  const handleCompleteChallenge = async (id) => {
    setCompleting(id);
    try {
      await apiFetchJson(instance, `/api/gamification/challenges/${id}/complete`, { method: 'POST' });
      setChallenges(prev => prev.map(c => c.id === id ? { ...c, isCompleted: true } : c));
    } catch (err) {
      setError(err.message || 'Failed to complete challenge');
    } finally {
      setCompleting(null);
    }
  };

  const currentStreak = streak?.currentStreak ?? 0;
  const hasFreezeActive = streak?.freezeActive ?? false;
  const xpLevel = streak?.xpLevel ?? 1;
  const xpCurrent = streak?.xpCurrent ?? 0;
  const xpNext = streak?.xpNextLevel ?? 100;

  if (loading) return <div className="streaks-loading">{t('bookClub.streaks.loading')}</div>;
  if (error) return <div className="streaks-error">{error} <button onClick={fetchData}>{t('bookClub.common.retry')}</button></div>;

  const dailyChallenges = challenges.filter(c => c.type === 'daily' || c.frequency === 'daily');
  const weeklyChallenges = challenges.filter(c => c.type === 'weekly' || c.frequency === 'weekly');
  const otherChallenges = challenges.filter(c =>
    !['daily', 'weekly'].includes(c.type) && !['daily', 'weekly'].includes(c.frequency)
  );

  return (
    <div className="streaks-container">
      {/* Streak Counter */}
      <div className="streaks-counter-card">
        <div className="streaks-fire-section">
          <span className="streaks-fire-emoji" role="img" aria-label="fire">🔥</span>
          <span className="streaks-count">{currentStreak}</span>
          <span className="streaks-label">{t('bookClub.streaks.dayStreak')}</span>
        </div>
        {hasFreezeActive && (
          <div className="streaks-freeze-badge">
            <span role="img" aria-label="freeze">🧊</span> {t('bookClub.streaks.freezeActive')}
          </div>
        )}
        <button
          className="streaks-record-btn"
          onClick={handleRecordStreak}
          disabled={recording || streak?.recordedToday}
        >
          {recording ? t('bookClub.streaks.recording') : streak?.recordedToday ? '✓ ' + t('bookClub.streaks.loggedToday') : t('bookClub.streaks.logTodayReading')}
        </button>
        <p className="streaks-auto-note">{t('bookClub.streaks.autoHint')}</p>
      </div>

      {/* XP Level */}
      <div className="streaks-xp-card">
        <div className="streaks-xp-header">
          <span className="streaks-xp-badge">{t('bookClub.streaks.level')} {xpLevel}</span>
          <span className="streaks-xp-progress-text">{xpCurrent} / {xpNext} {t('bookClub.streaks.xp')}</span>
        </div>
        <div className="streaks-xp-bar">
          <div
            className="streaks-xp-bar-fill"
            style={{ width: `${xpNext > 0 ? (xpCurrent / xpNext) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Challenges */}
      <div className="streaks-challenges-section">
        <h3 className="streaks-section-title">{t('bookClub.streaks.challenges')}</h3>

        {dailyChallenges.length > 0 && (
          <div className="streaks-challenge-group">
            <h4 className="streaks-group-label">{t('bookClub.streaks.daily')}</h4>
            {dailyChallenges.map(c => (
              <ChallengeCard
                key={c.id}
                challenge={c}
                onComplete={handleCompleteChallenge}
                completing={completing}
              />
            ))}
          </div>
        )}

        {weeklyChallenges.length > 0 && (
          <div className="streaks-challenge-group">
            <h4 className="streaks-group-label">{t('bookClub.streaks.weekly')}</h4>
            {weeklyChallenges.map(c => (
              <ChallengeCard
                key={c.id}
                challenge={c}
                onComplete={handleCompleteChallenge}
                completing={completing}
              />
            ))}
          </div>
        )}

        {otherChallenges.length > 0 && (
          <div className="streaks-challenge-group">
            <h4 className="streaks-group-label">{t('bookClub.streaks.other')}</h4>
            {otherChallenges.map(c => (
              <ChallengeCard
                key={c.id}
                challenge={c}
                onComplete={handleCompleteChallenge}
                completing={completing}
              />
            ))}
          </div>
        )}

        {challenges.length === 0 && (
          <p className="streaks-empty">{t('bookClub.streaks.noChallenges')}</p>
        )}
      </div>
    </div>
  );
}

function ChallengeCard({ challenge, onComplete, completing }) {
  const { t } = useTranslation();
  const { id, title, description, xpReward, isCompleted } = challenge;
  return (
    <div className={`streaks-challenge-card ${isCompleted ? 'completed' : ''}`}>
      <div className="streaks-challenge-check">
        {isCompleted ? '✅' : '⬜'}
      </div>
      <div className="streaks-challenge-info">
        <span className="streaks-challenge-title">{title || 'Challenge'}</span>
        {description && <span className="streaks-challenge-desc">{description}</span>}
      </div>
      <span className="streaks-xp-pill">{xpReward ?? 0} XP</span>
      {!isCompleted && (
        <button
          className="streaks-complete-btn"
          onClick={() => onComplete(id)}
          disabled={completing === id}
        >
          {completing === id ? '…' : t('bookClub.streaks.complete')}
        </button>
      )}
    </div>
  );
}

export default StreaksChallenges;
